import { useState, useEffect } from "react";
import { blogService } from "../../services/blogService";
import type { BlogRevision } from "../../types/blog";
import { CollapsibleSection } from "../../components/CollapsibleSection";

interface RevisionListProps {
    blogId: string | number;
    isOpen?: boolean;
    onToggle?: () => void;
    refreshKey?: number;
    loadedRevisionId?: number | null;
    hasUnsavedChanges?: boolean;
    onSelect: (revision: BlogRevision) => void;
}

function formatRevisionDate(iso: string) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function revisionPreview(content: string) {
    const line = content.split("\n").find((part) => part.trim())?.trim() ?? "";
    if (line.length <= 80) return line || "Empty snapshot";
    return `${line.slice(0, 77)}...`;
}

export function RevisionList({
    blogId,
    isOpen,
    onToggle,
    refreshKey,
    loadedRevisionId,
    hasUnsavedChanges,
    onSelect,
}: RevisionListProps) {
    const [revisions, setRevisions] = useState<BlogRevision[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!blogId) return;

        let cancelled = false;
        const fetchRevisions = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const next = await blogService.listRevisions(blogId);
                if (!cancelled) setRevisions(next);
            } catch (err) {
                console.error("Failed to fetch revisions", err);
                if (!cancelled) setError("Failed to load revisions");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetchRevisions();
        return () => {
            cancelled = true;
        };
    }, [blogId, refreshKey]);

    const handleSelect = (revision: BlogRevision) => {
        if (
            hasUnsavedChanges &&
            loadedRevisionId !== revision.id &&
            !window.confirm("Load this revision? Unsaved changes in the editor will be replaced.")
        ) {
            return;
        }
        onSelect(revision);
    };

    return (
        <CollapsibleSection
            title="Revisions"
            isOpen={isOpen}
            onToggle={onToggle}
        >
            <div className="flex flex-col gap-3 border-t border-base-content/5 pt-3 min-h-0">
                <div className="flex justify-between items-center">
                    <span className="text-xs opacity-40 italic">
                        {revisions.length} {revisions.length === 1 ? "snapshot" : "snapshots"}
                    </span>
                </div>

                {isLoading && revisions.length === 0 ? (
                    <div className="flex justify-center py-8">
                        <span className="loading loading-spinner loading-sm text-primary"></span>
                    </div>
                ) : error ? (
                    <div className="text-error text-xs text-center py-2">{error}</div>
                ) : revisions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-10 px-4 rounded-xl border border-dashed border-base-content/20 text-sm opacity-60 text-center">
                        No snapshots yet. Saving content creates one of the previous version.
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        {revisions.map((revision) => {
                            const isLoaded = loadedRevisionId === revision.id;
                            return (
                                <div
                                    key={revision.id}
                                    className={`rounded-xl bg-base-200 border p-2.5 flex flex-col gap-2 ${isLoaded ? "border-primary/40" : "border-base-content/5"}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex flex-col gap-0.5">
                                            <span className="text-xs font-medium">
                                                {formatRevisionDate(revision.created_at)}
                                            </span>
                                            <span className="text-[11px] opacity-50 truncate" title={revision.note}>
                                                {revision.note}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className={`btn btn-xs shrink-0 ${isLoaded ? "btn-primary" : "btn-ghost"}`}
                                            onClick={() => handleSelect(revision)}
                                            disabled={isLoaded}
                                        >
                                            {isLoaded ? "Loaded" : "Load"}
                                        </button>
                                    </div>
                                    <p className="text-[11px] opacity-60 line-clamp-2 font-mono leading-relaxed">
                                        {revisionPreview(revision.content)}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </CollapsibleSection>
    );
}
